/** Draws only the rows of a long table that are on screen, with two spacer rows standing in for the rest.
 *
 * A scope table over a mid-size customer holds two thousand figures, each with a meter, a chain of
 * dots and three chips; drawing all of them is a six-figure DOM-node count and a page that stutters
 * when scrolled. Rows are native `tr`s in a native `table` (the grid's accessibility depends on it), so
 * this is not an absolutely positioned list: the rows outside the window are replaced by one empty row
 * above and one below, whose heights are the estimated height of the rows they stand for.
 *
 * - **Below `WINDOW_AT` rows, nothing changes**: every row is drawn.
 * - **The window is computed from geometry**, the intersection of the scroll area with the browser's
 *   viewport, so it is right whether the grid scrolls inside its card or the page scrolls around it.
 * - **Where there is no layout** (a test's DOM, a hidden tab: the scroll area has no height) every row is
 *   drawn, so nothing that is measured by a test or read by a screen reader goes missing.
 * - The row height is **measured** from the rows drawn (the median), so a density change or a two-line
 *   cell moves the spacers rather than leaving them wrong.
 * - A row that the keyboard moves to but is outside the window is revealed first (`reveal`), then focused
 *   by the caller once it exists.
 *
 * Everything that reads the page runs in a frame callback or an event handler, never in render.
 */

import { useEffect, useState } from "react";
import type { RefObject } from "react";

/** Below this many rows (groups included) every row is drawn. */
export const WINDOW_AT = 120;
/** Rows drawn beyond each edge of the viewport. */
const OVERSCAN = 14;
/** The window is recomputed only when the viewport comes within this many rows of an edge of it. */
const SLACK = 5;
const INITIAL_ROWS = 40;

type Mode = "pending" | "live" | "off";

export interface RowWindow {
  /** Whether rows are being left out at all. */
  active: boolean;
  /** The first and one-past-the-last item drawn. */
  start: number;
  end: number;
  /** The height, in px, of the rows left out above and below. */
  top: number;
  bottom: number;
  /** Draw this item (and the rows around it): the keyboard is moving there. */
  reveal: (item: number) => void;
}

function median(values: number[]): number {
  const sorted = [...values].sort((a, b) => a - b);
  return sorted[Math.floor(sorted.length / 2)] ?? 0;
}

export function useRowWindow(
  count: number,
  scroller: RefObject<HTMLElement | null>,
  tbody: RefObject<HTMLTableSectionElement | null>,
  fallbackHeight: number,
): RowWindow {
  const big = count > WINDOW_AT;
  const [mode, setMode] = useState<Mode>("pending");
  const [range, setRange] = useState({ start: 0, end: INITIAL_ROWS });
  const [measured, setMeasured] = useState<number | null>(null);
  const rowHeight = measured ?? fallbackHeight;

  useEffect(() => {
    if (!big) return;
    let frame: number | null = null;

    const place = () => {
      frame = null;
      const area = scroller.current;
      const body = tbody.current;
      if (!area || !body) return;
      if (area.clientHeight === 0) {
        setMode("off");
        return;
      }
      setMode("live");
      // The height of a row, as drawn: the median of the first few, spacers excluded.
      const heights: number[] = [];
      for (const row of body.rows) {
        if (row.hasAttribute("data-spacer")) continue;
        if (row.offsetHeight > 0) heights.push(row.offsetHeight);
        if (heights.length >= 15) break;
      }
      const h = median(heights) || rowHeight;
      if (Math.abs(h - rowHeight) > 0.5) setMeasured(h);
      const a = area.getBoundingClientRect();
      const b = body.getBoundingClientRect();
      const viewTop = Math.max(a.top, 0);
      const viewBottom = Math.min(a.bottom, window.innerHeight);
      if (viewBottom <= viewTop) return;
      const first = Math.max(0, Math.floor((viewTop - b.top) / h));
      const last = Math.min(count, Math.ceil((viewBottom - b.top) / h));
      setRange((current) => {
        const covered = current.start <= Math.max(0, first - SLACK) && current.end >= Math.min(count, last + SLACK);
        if (covered) return current;
        // An even start keeps a row's zebra shade when the window moves.
        const start = Math.max(0, first - OVERSCAN) & ~1;
        const end = Math.min(count, last + OVERSCAN);
        return start === current.start && end === current.end ? current : { start, end };
      });
    };

    const schedule = () => {
      if (frame === null) frame = requestAnimationFrame(place);
    };
    // Captured at the document so any scrolling ancestor is heard, not only the grid's own area.
    document.addEventListener("scroll", schedule, { capture: true, passive: true });
    window.addEventListener("resize", schedule);
    const observer = typeof ResizeObserver === "undefined" ? undefined : new ResizeObserver(schedule);
    if (scroller.current) observer?.observe(scroller.current);
    // Once now (after the rows just drawn are laid out), and again whenever the window or the row
    // height moves: a window that was too short for the viewport is widened on the next frame.
    schedule();
    return () => {
      document.removeEventListener("scroll", schedule, { capture: true });
      window.removeEventListener("resize", schedule);
      observer?.disconnect();
      if (frame !== null) cancelAnimationFrame(frame);
    };
  }, [big, count, range.start, range.end, rowHeight, scroller, tbody]);

  const reveal = (item: number) => {
    setRange({
      start: Math.max(0, item - OVERSCAN) & ~1,
      end: Math.min(count, item + OVERSCAN + 1),
    });
  };

  if (!big || mode === "off") {
    return { active: false, start: 0, end: count, top: 0, bottom: 0, reveal };
  }
  const end = Math.min(count, range.end);
  const start = Math.min(range.start, end);
  return {
    active: true,
    start,
    end,
    top: start * rowHeight,
    bottom: (count - end) * rowHeight,
    reveal,
  };
}
